// externals
import { memo, useRef } from 'react';
import { useSelector } from 'react-redux';

// libraries
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';

// components
import TradeVinDecode from '../TradeVinDecode';

import { BodyStyleField } from '../vehicle/BodyStyleField.interstate';
import { InteriorColorField } from '../vehicle/InteriorColorField.interstate';
import { EngineField } from '../vehicle/EngineField.interstate';
import { TransmissionField } from '../vehicle/TransmissionField.interstate';
import { DriveLineField } from '../vehicle/DriveLineField.interstate';
import YearsField from '../vehicle/YearsField.interstate';
import MakesField from '../vehicle/MakesField.interstate';
import ModelsField from '../vehicle/ModelsField.interstate';
import TrimsField from '../vehicle/TrimsField.interstate';
import { OdometerField } from '../vehicle/OdometerField.interstate';
import { StockNumberField } from '../vehicle/StockNumberField.interstate';
import ExteriorColorField from '../vehicle/ExteriorColorField.interstate';
import VehicleConditionField from '../vehicle/VehicleConditionField.interstate';
import { Grid } from '@interstate/components/Grid';

// hooks
import { useScrollIntoView } from '../../../utils/useScrollIntoView';

import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { tradeInContainerGridProps, tradeInContainerLargeFieldsProps } from '../lienholderForm/utils';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { useMediaQuery } from 'react-responsive';
import { StyledOdometerGridContainer } from '../trade.style';

const ManualTradeIn = memo(({ isEditDisabled }: { isEditDisabled?: boolean }) => {
    const elementRef = useRef<HTMLDivElement>(null);

    const hasTradeIn = useSelector(tradeInSelectors.isTradeInCompleted);
    const isManualFormOpen = true;
    const isBreakdownOpen = useSelector(baseDealSelectors.getBreakdownStatus);
    const isLargeScreen = useMediaQuery({ query: `(min-width: 1360px)` });
    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);
    const isCompactUI = useSelector(baseDealSelectors.getCurrentSlideOut) === baseDealActionCreators.SlideOutType.TradeIn;
    const isLocked = dealLockHooks.useIsDealLocked();
    const { xs, sm, md, lg, xl } = tradeInContainerGridProps(
        isBreakdownOpen,
        isLargeScreen,
        isDealCentralMultiScenario,
        isCompactUI
    );
    const largeFieldProps = tradeInContainerLargeFieldsProps(
        isBreakdownOpen,
        isLargeScreen,
        isDealCentralMultiScenario,
        isCompactUI
    );
    useScrollIntoView(elementRef, isManualFormOpen);

    return (
        <div ref={elementRef}>
            {
                <Grid
                    justifyContent={'flex-start'}
                    container
                    flexDirection="row"
                    columnSpacing={isDealCentralMultiScenario ? 1 : { xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
                    rowGap="1rem"
                >
                    {isManualFormOpen && (
                        <Grid {...largeFieldProps}>
                            {isManualFormOpen && <TradeVinDecode isManualFormOpen={isManualFormOpen} disabled={isEditDisabled} />}
                        </Grid>
                    )}
                    <StyledOdometerGridContainer sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <OdometerField
                            isManualFormOpen={isManualFormOpen}
                            hasTradeIn={hasTradeIn}
                            disabled={isLocked || isEditDisabled}
                        />
                    </StyledOdometerGridContainer>
                    {isManualFormOpen && (
                        <>
                            <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                                <YearsField isManualFormOpen={isManualFormOpen} disabled={isEditDisabled} />
                            </Grid>
                            <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                                <MakesField isManualFormOpen={isManualFormOpen} disabled={isEditDisabled} />
                            </Grid>
                            <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                                <ModelsField isManualFormOpen={isManualFormOpen} disabled={isEditDisabled} />
                            </Grid>
                            <Grid {...largeFieldProps}>
                                <TrimsField isManualFormOpen={isManualFormOpen} disabled={isEditDisabled} />
                            </Grid>
                        </>
                    )}
                    <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <ExteriorColorField isManualFormOpen={isManualFormOpen} hasTradeIn={hasTradeIn} disabled={isEditDisabled} />
                    </Grid>
                    <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <VehicleConditionField
                            isManualFormOpen={isManualFormOpen}
                            hasTradeIn={hasTradeIn}
                            disabled={isEditDisabled}
                        />
                    </Grid>
                    <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <EngineField isManualFormOpen={isManualFormOpen} hasTradeIn={hasTradeIn} disabled={isEditDisabled} />
                    </Grid>
                    <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <TransmissionField isManualFormOpen={isManualFormOpen} hasTradeIn={hasTradeIn} disabled={isEditDisabled} />
                    </Grid>
                    <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <DriveLineField isManualFormOpen={isManualFormOpen} hasTradeIn={hasTradeIn} disabled={isEditDisabled} />
                    </Grid>
                    <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <BodyStyleField isManualFormOpen={isManualFormOpen} hasTradeIn={hasTradeIn} disabled={isEditDisabled} />
                    </Grid>
                    <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <InteriorColorField isManualFormOpen={isManualFormOpen} hasTradeIn={hasTradeIn} disabled={isEditDisabled} />
                    </Grid>
                    <Grid sm={sm} xs={xs} md={md} lg={lg} xl={xl}>
                        <StockNumberField isManualFormOpen={isManualFormOpen} hasTradeIn={hasTradeIn} disabled={isEditDisabled} />
                    </Grid>
                </Grid>
            }
        </div>
    );
});

export default ManualTradeIn;
